<template>
  <v-form v-model="valid" ref="mainForm">
    <div v-show="!estPsychotherapeuteNonMembre">
      <ressource code="message-cessionnaires-non-pcna"></ressource>
    </div>
    <div v-show="estPsychotherapeuteNonMembre">
      <ressource code="message-introduction-cessionnaires"></ressource>
      
      <opq-catalogue
        code="type-cessionnaire"
        v-model="sasInscriptionTableauRegistre.typeCessionnaire"
        :live="liveSasInscriptionTableauRegistre(2324)"
        :filter="filterTypeCessionnaire"
        orderBy="code"
        :optional="!estPsychotherapeuteNonMembre"
        @input="onTypeCessionnaireChange"
      >
      </opq-catalogue>

      <ressource v-show="typeCessionnaireEstCessionnaires" code="description-cessionnaires"></ressource>

      <!-- Liste des cessionnaires - visible uniquement si "J'ai un ou des cessionnaires" -->      
      <data-iterator
        v-show="typeCessionnaireEstCessionnaires"
        :list="sas.cessionnaires"
        add-new-text="Ajouter un cessionnaire"
        :live="{ endpoint: 'sas-cessionnaire', sasId: sas.id }"
        :icon="$getIcon('account')"
        :valids="valids"
        ref="dataIterator"
        :undeletable="(item) => item.inactif"
        :optional="!typeCessionnaireEstCessionnaires"
        hideDots
      >
        <template v-slot:show-mode="{ item }">
          <h3>{{ item.nomComplet || "Cessionnaire" }}</h3>
          <p class="my-0">Numéro de permis: {{ item.numeroPermis || "N/A" }}</p>
          <!-- Case à cocher pour archiver -->
          <opq-case
            v-if="item.contactCessionnaire && item.inactif"
            code="cessionnaire-archiver"
            v-model="item.archive"
            :live="liveSasCessionnaire(25103, item)"
            optional
            :ref="'cessionnaire-archiver-' + item.id"
          ></opq-case>
        </template>
        <template v-slot:edit-mode="{ item }">
          <!-- Recherche de membre par numéro de permis -->
          <search-membre
            v-if="!item.inactif"
            @membre-selected="membreSelected($event, item.id)"
            label="Rechercher un membre par numéro de permis"
          ></search-membre>

          <!-- Champ caché pour stocker l'ID du contact cessionnaire -->
          <opq-text-field
            code="cessionnaire-contact-id"
            v-show="false"
            v-model="item.contactCessionnaire"
            :live="liveSasCessionnaire(25104, item)"
            :ref="'contactCessionnaire' + item.id"
          ></opq-text-field>

          <!-- Affichage du nom et numéro de permis (lecture seule une fois sélectionné) -->
          <div v-if="item.contactCessionnaire">
            <h3>{{ item.nomComplet }}</h3>
            <p class="my-0">Numéro de permis: {{ item.numeroPermis || "N/A" }}</p>
          </div>
        </template>
      </data-iterator>
      <v-checkbox
        class="list_counter_validator my-2 ml-6 p-0"
        :input-value="auMoinsUnCessionnaire"
        :rules="[(v) => !!v || 'Vous devez avoir au moins un cessionnaire']"
      ></v-checkbox>
    </div>
  </v-form>
</template>

<script>
import CataloguesHelper from "@/mixins/CataloguesHelpers"
import FormHybrid from "@/mixins/FormHybrid"
import RessourcesHelpers from "@/mixins/RessourcesHelpers"
import SearchMembre from "@/components/Common/SearchMembre"

export default {
  mixins: [CataloguesHelper, FormHybrid, RessourcesHelpers],
  components: { SearchMembre },
  props: {
    sas: { type: Object },
  },
  data() {
    return {
      previousTypeCessionnaire: null,
    }
  },
  mounted() {
    this.previousTypeCessionnaire = this.sasInscriptionTableauRegistre.typeCessionnaire
  },
  methods: {
    liveSasInscriptionTableauRegistre(cle) {
      return { endpoint: "sas-inscriptions-tableau-registre", cle: cle, fileId: this.sasInscriptionTableauRegistre.id }
    },
    liveSasCessionnaire(cle, item) {
      return { endpoint: "sas-cessionnaire", cle: cle, fileId: item.id }
    },
    estCategorie(codeCategorie) {
      return (
        this.getCatalogueCodeById(11124, this.sas.sasInscriptionAuTableauRegistre.typeDeCotisation) == codeCategorie
      )
    },
    estTypeCessionnaire(codeTypeCessionnaire) {
      return (
        this.sasInscriptionTableauRegistre.typeCessionnaire
        && this.getCatalogueCodeById(2324, this.sasInscriptionTableauRegistre.typeCessionnaire).includes(codeTypeCessionnaire)
      )
    },

    /**
     * Filtre les options de type cessionnaire selon la catégorie de membre
     */
    filterTypeCessionnaire(fd) {
      // Options 1 et 2 uniquement pour retraités
      if (fd.code.includes("NODOS") || fd.code.includes("GARDOS")) {
        return this.estCategorie(15) // 15 = Retraité
      }

      // Option 3 disponible pour tous
      if (fd.code.includes("CES")) {
        return true
      }

      // Options 4 et 5 disponibles pour tous
      if (fd.code.includes("ORGDOS") || fd.code.includes("AUCUN")) {
        return true
      }

      return false
    },

    /**
     * Gère le changement de type de cessionnaire
     */
    onTypeCessionnaireChange(newValue) {
      // si on choisi une autre option que "J'ai un ou des cessionnaires", on archive tous les cessionnaires existants
      if (!this.typeCessionnaireEstCessionnaires)
      {
        this.sas.cessionnaires.filter((item) => item.inactif).every((item) => {
          let caseArchiver = this.$refs["cessionnaire-archiver-" + item.id]
          if (caseArchiver) {
            caseArchiver.setValue(true)
          }
        })
      }
    },

    /**
     * Callback quand un membre est sélectionné via la recherche
     */
    membreSelected(membre, itemId) {
      const item = this.sas.cessionnaires.find((c) => c.id === itemId)
      if (item) {
        item.nomComplet = membre.nomComplet
        item.numeroPermis = membre.numeroPermis

        this.$refs["contactCessionnaire" + itemId].setValue(membre.id)
      }
    },
  },
  computed: {
    sasInscriptionTableauRegistre() {
      return this.sas.sasInscriptionAuTableauRegistre
    },
    estPsychotherapeuteNonMembre() {
      return this.estCategorie(23) || this.estCategorie(24)
    },
    /**
     * Vérifie si le type est "J'ai un ou des cessionnaires"
     */
    typeCessionnaireEstCessionnaires() {
      return this.estTypeCessionnaire("CES")
    },
    auMoinsUnCessionnaire() {
      return !this.typeCessionnaireEstCessionnaires
              || (this.typeCessionnaireEstCessionnaires
                && this.sas.cessionnaires.some((item) => !item.archive))
    },
  }
}
</script>

<style>
</style>
